import { RowItemType, Section, SelectorIdentifier, UIElementType } from '../../models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import * as actions from '../../actions';
import { ChannelElement, ComportOption, MonitoringPointElement, SampleRateElement, SensorElement, SensorPreference } from '../../../models/location-card.model';
import { TritonSectionIds, TritonInfoFieldId } from './base-config-models';
import { extractTritonSensorsFromConfig, getMPvalueBySensorName, getTritonSensorValue } from './utils';
import { CommportValue, MonitorSeriesNames, NoneSensor } from '../../../location-card.constants';
import { Action } from '@ngrx/store';
import { field } from '../../core/field-schema';
import { pattern, min, max, required } from '../../core/validator-builders';
import { TritonLocationCardState } from './models';

/**
 * Triton Info Configuration Section
 */
export const tritoninfo = (state: TritonLocationCardState): Section => {
    const settings = state.settings;
    const details = state.locationDetails;
    const config = state.monitorConfiguration;

    const isEditMode = details && details.length > 0 && details[0].series === MonitorSeriesNames.triton;
    const [sensor1, sensor2] = extractTritonSensorsFromConfig(config);
    const comportValue = isEditMode ? getComportValue(config.comport) : CommportValue.None;
    const sensor1Required = comportValue === CommportValue.None;
    const tiltRequired = comportValue === CommportValue.Inclinometer;

    const [mp1Info, mp2Info] = details ? details : [];

    return {
        info: {
            id: TritonSectionIds.TritonInfo,
            title: 'ADD_EDIT.ADD_EDIT_TRITON',
            displayTitle: true,
            expandable: true,
            expanded: false,
            panelClass: !state.permissions.isMonitorEditor ? 'disable-field' : undefined,
        },
        rows: [
            // Sensor 1 Title Row
            {
                info: {
                    id: 'sensor1-title-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: 'sensor1-title',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'sensor1Title',
                        label: 'ADD_EDIT.ADD_EDIT_SUBTITLE',
                        panelClass: 'font-600 section-subtitle subtitle-text font-600',
                    },
                ],
            },
            
            // Store Temp and Battery Voltage row
            {
                info: {
                    id: 'store-temp-battery-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.toggle(TritonInfoFieldId.StoreTemperature, 'storetemperature', {
                        label: 'ADD_EDIT.STORE_MONITOR_TEMPERATURE',
                        panelClass: 'each-Item item-flex-center',
                        value: config ? config.storetemperature : false,
                    }),
                    field.select<SampleRateElement, number>(TritonInfoFieldId.BatteryVoltage, 'storebattery', {
                        placeholder: 'ADD_EDIT.STORE_BATTERY_VOLTAGE',
                        value: config?.storebattery ?? 1,
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.BatteryVoltageOptions,
                        panelClass: 'each-Item',
                    }),
                ],
            },

            // Sensor 1 Fields Row
            {
                info: {
                    id: 'sensor1-fields-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.select<SensorElement, string>(TritonInfoFieldId.Sensor1, 'sensor', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SENSOR',
                        value: isEditMode
                            ? sensor1
                                ? getTritonSensorValue(sensor1).value
                                : NoneSensor.value
                            : null,
                        validators: sensor1Required ? [required()] : [],
                        panelClass: 'width-30',
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        events: {
                            onChange: (sensor: SensorElement) => [actions.tritonSensorChange({ sensor, index: 0 })],
                        },
                        optionsSelector: SelectorIdentifier.PrimarySensors,
                    }),
                    field.select<MonitoringPointElement, number>(TritonInfoFieldId.MP1, 'mp', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MONITORING_POINT',
                        value: isEditMode && sensor1 ? getMPvalueBySensorName(sensor1.name) : null,
                        panelClass: 'width-30',
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        validators: sensor1Required ? [required()] : [],
                        events: {
                            onChange: () => [actions.tritonMonitorPointChange({ index: 0 })],
                        },
                        optionsSelector: SelectorIdentifier.MonitoringPoints,
                    }),
                    field.select<ChannelElement, number>(TritonInfoFieldId.Channel1, 'channel', {
                        panelClass: 'width-30',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_CHANNEL',
                        value: isEditMode && sensor1 ? sensor1.channel : null,
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        validators: sensor1Required ? [required()] : [],
                        events: {
                            onChange: () => [actions.tritonChannelChange({ index: 0 })],
                        },
                        optionsSelector: SelectorIdentifier.Channels,
                    }),
                ],
            },

            // Sensor 1 Silt Row
            {
                info: {
                    id: 'sensor1-silt-row',
                    panelClass: 'flex custom-flat-button',
                },
                items: [
                    field.number(TritonInfoFieldId.Silt1, 'silt', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SILT',
                        readonly: true,
                        placeholderSuffix: settings.depthUnit,
                        value: isEditMode && config.silts && config.silts.length ? config.silts[0] : 0,
                        panelClass: 'slide-active-display',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.installationValuePattern,
                                message: settings.isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                    }),
                    {
                        id: TritonInfoFieldId.SiltEditButton1,
                        type: RowItemType.UI,
                        uiType: UIElementType.Button,
                        visible: state.permissions.isBasicDataEditingAllowed,
                        disabled: !isEditMode,
                        name: 'siltEditButton1',
                        iconName: 'edit',
                        tooltip: 'COMMON.SILT_EDITOR',
                        panelClass: 'no-margin',
                        events: {
                            onClick: () => [
                                actions.openSiltDialog({ updateFieldId: TritonInfoFieldId.Silt1, mpIndex: 0 }),
                            ],
                        },
                    },
                ],
            },

            // Divider
            {
                info: {
                    id: 'divider-row',
                    panelClass: '',
                },
                items: [
                    {
                        id: 'divider',
                        type: RowItemType.UI,
                        uiType: UIElementType.Divider,
                        name: 'divider',
                        dividerStyle: 'solid',
                        dividerThickness: 1,
                        dividerColor: '#e0e0e0',
                        panelClass: 'divider margin-175-0',
                    },
                ],
            },

            // Sensor 2 Title Row
            {
                info: {
                    id: 'sensor2-title-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: 'sensor2-title',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'sensor2Title',
                        label: 'ADD_EDIT.ADD_EDIT_SECOND_SUBTITLE',
                        panelClass: 'section-subtitle subtitle-text font-600',
                    },
                ],
            },

            // Sensor 2 Fields Row
            {
                info: {
                    id: 'sensor2-fields-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.select<SensorElement, string>(TritonInfoFieldId.Sensor2, 'sensor', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SENSOR',
                        panelClass: 'width-30',
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        value: isEditMode && sensor2 ? getTritonSensorValue(sensor2, false).value : null,
                        events: {
                            onChange: (sensor: SensorElement) => {
                                const toDispatch: Action[] = [actions.tritonSensorChange({ sensor, index: 1 })];

                                // not None
                                if (sensor.value !== '') {
                                    toDispatch.push(
                                        actions.enableField({ fieldId: TritonInfoFieldId.MP2 }),
                                        actions.enableField({ fieldId: TritonInfoFieldId.Channel2 }),
                                        actions.enableField({ fieldId: TritonInfoFieldId.Silt2 }),
                                    );
                                }

                                return toDispatch;
                            },
                        },
                        optionsSelector: SelectorIdentifier.SecondarySensors,
                    }),
                    field.select<MonitoringPointElement, number>(TritonInfoFieldId.MP2, 'mp', {
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MONITORING_POINT',
                        panelClass: 'width-30',
                        value: isEditMode && sensor2 ? getMPvalueBySensorName(sensor2.name) : null,
                        disabled: isEditMode && sensor2 ? false : true,
                        events: {
                            onChange: () => [actions.tritonMonitorPointChange({ index: 1 })],
                        },
                        optionsSelector: SelectorIdentifier.MonitoringPoints,
                    }),
                    field.select<ChannelElement, number>(TritonInfoFieldId.Channel2, 'channel', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_CHANNEL',
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        panelClass: 'width-30',
                        value: isEditMode && sensor2 ? sensor2.channel : null,
                        disabled: isEditMode && sensor2 ? false : true,
                        events: {
                            onChange: () => [actions.tritonChannelChange({ index: 1 })],
                        },
                        optionsSelector: SelectorIdentifier.Channels,
                    }),
                ],
            },

            // Sensor 2 Silt Row
            {
                info: {
                    id: 'sensor2-silt-row',
                    panelClass: 'flex custom-flat-button',
                },
                items: [
                    field.number(TritonInfoFieldId.Silt2, 'silt', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SILT',
                        placeholderSuffix: settings.depthUnit,
                        readonly: true,
                        value: isEditMode ? config.silts[1] : 0,
                        disabled: isEditMode && sensor2 ? false : true,
                        panelClass: 'slide-active-display',
                        validators: [
                            pattern({
                                value: REGEX_CONFIG.installationValuePattern,
                                message: settings.isMetric ? 'COMMON.OFFSET_METRIC_ERROR' : 'COMMON.OFFSET_NON_METRIC_ERROR',
                            }),
                        ],
                    }),
                    {
                        id: TritonInfoFieldId.SiltEditButton2,
                        type: RowItemType.UI,
                        uiType: UIElementType.Button,
                        name: 'siltEditButton2',
                        visible: state.permissions.isBasicDataEditingAllowed,
                        iconName: 'edit',
                        tooltip: 'COMMON.SILT_EDITOR',
                        disabled: !isEditMode,
                        panelClass: 'no-margin',
                        events: {
                            onClick: () => [
                                actions.openSiltDialog({ updateFieldId: TritonInfoFieldId.Silt2, mpIndex: 1 }),
                            ],
                        },
                    },
                ],
            },
            {
                info: {
                    id: 'sensor-preference-title-row-1',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: TritonInfoFieldId.SensorPreferencesTitle,
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'sensorPreferenceTitle',
                        label: 'ADD_EDIT.MP1_SENSOR_PREFERENCE',
                        panelClass: 'section-subtitle subtitle-text font-600',
                    },
                ],
            },
            {
                info: {
                    id: 'sensor-preference-divider-row-1',
                    panelClass: '',
                },
                items: [
                    {
                        id: 'sensor-preference-divider-1',
                        type: RowItemType.UI,
                        uiType: UIElementType.Divider,
                        name: 'sensorPreferenceDivider-1',
                        dividerStyle: 'solid',
                        dividerThickness: 1,
                        dividerColor: '#e0e0e0',
                        panelClass: 'divider margin-05-0',
                    },
                ],
            },
            {
                info: {
                    id: 'sensor-preference-fields-row-1',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.select<SensorPreference, number>(TritonInfoFieldId.DepthSensor1, 'depthSensor', {
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_DEPTH',
                        panelClass: 'each-Item',
                        value: isEditMode ? mp1Info.depthSensor : 100,
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        optionsSelector: SelectorIdentifier.DepthSensorPreferencesCh1,
                    }),
                    field.select<SensorPreference, number>(TritonInfoFieldId.VelocitySensor1, 'velocitySensor', {
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_VELOCITY',
                        panelClass: 'each-Item',
                        value: isEditMode ? mp1Info.velocitySensor : 100,
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        optionsSelector: SelectorIdentifier.VelocitySensorPreferencesCh1,
                    }),
                ],
            },
            {
                info: {
                    id: 'sensor-preference-title-row-2',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: TritonInfoFieldId.SensorPreferencesTitle2,
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'sensorPreferenceTitle',
                        label: 'ADD_EDIT.MP2_SENSOR_PREFERENCE',
                        panelClass: 'font-600 section-subtitle subtitle-text',
                    },
                ],
            },
            {
                info: {
                    id: 'sensor-preference-divider-row-2',
                    panelClass: '',
                },
                items: [
                    {
                        id: TritonInfoFieldId.SensorPreferenceDivider,
                        type: RowItemType.UI,
                        uiType: UIElementType.Divider,
                        name: 'sensorPreferenceDivider',
                        dividerStyle: 'solid',
                        dividerThickness: 1,
                        dividerColor: '#e0e0e0',
                        panelClass: 'divider margin-05-0',
                    },
                ],
            },
            {
                info: {
                    id: 'sensor-preference-fields-row-2',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.select<SensorPreference, number>(TritonInfoFieldId.DepthSensor2, 'depthSensor2', {
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_DEPTH',
                        panelClass: 'each-Item',
                        value: isEditMode && mp2Info ? mp2Info.depthSensor : 100,
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        optionsSelector: SelectorIdentifier.DepthSensorPreferencesCh2,
                    }),
                    field.select<SensorPreference, number>(TritonInfoFieldId.VelocitySensor2, 'velocitySensor2', {
                        placeholder: 'ADD_EDIT.INACTIVE_TOGGLE_VELOCITY',
                        panelClass: 'each-Item',
                        value: isEditMode && mp2Info ? mp2Info.velocitySensor : 100,
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        optionsSelector: SelectorIdentifier.VelocitySensorPreferencesCh2,
                    }),
                ],
            },
            // Divider before Comm Port
            {
                info: {
                    id: 'comport-divider',
                    panelClass: '',
                },
                items: [
                    {
                        id: TritonInfoFieldId.ComportDivider,
                        type: RowItemType.UI,
                        uiType: UIElementType.Divider,
                        name: 'comportDivider',
                        dividerStyle: 'solid',
                        dividerThickness: 1,
                        dividerColor: '#e0e0e0',
                        panelClass: 'divider margin-075-0',
                    },
                ],
            },

            // Comm Port Section
            {
                info: {
                    id: 'comport-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<ComportOption, string>(TritonInfoFieldId.ComPort, 'comport', {
                        placeholder: 'ADD_EDIT.COMM_PORT',
                        value: isEditMode ? getComportValue(config.comport) : 'None',
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        panelClass: 'custom-width',
                        events: {
                            onChange: (option: { value: string }) => [
                                actions.commportChange({ commport: option.value }),
                            ],
                        },
                        optionsSelector: SelectorIdentifier.ComPorts,
                    }),
                    field.number(TritonInfoFieldId.TiltAlarmThreshold, 'inclinometerthreshold', {
                        placeholder: 'ADD_EDIT.TILT_ALARM_THRESHOLD',
                        value: isEditMode ? config.inclinometerthreshold ?? 5 : 5,
                        visible: tiltRequired,
                        panelClass: 'custom-width',
                        validators: [
                            ...(tiltRequired ? [required()] : []),
                            pattern({
                                value: REGEX_CONFIG.numericOnlyPattern,
                                message: '',
                            }),
                            min({
                                value: 1,
                                message: '',
                            }),
                            max({
                                value: 90,
                                message: 'ADD_EDIT.TILT_ALARM_THRESHOLD_ERROR_MESSAGE',
                            }),
                        ],
                    }),
                ],
            },
            {
                info: {
                    id: 'same-sensor-error',
                    panelClass: '',
                },
                items: [
                    {
                        id: TritonInfoFieldId.SameSensorError,
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'sameSensorError',
                        visible: false,
                        panelClass: 'mat-error mt-3',
                        label: 'ADD_EDIT.LC_SAME_SENSORS_ERROR',
                    },
                ],
            },
        ],
    };
};

function getComportValue(comport: number | string) {
    if (typeof comport === 'string') {
        return comport;
    }

    return [CommportValue.None, CommportValue.Inclinometer, CommportValue.Sampler][comport];
}
